import { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Grid,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  CircularProgress,
  Button,
} from '@material-ui/core';
import GetAppIcon from '@material-ui/icons/GetApp';
import PictureAsPdfIcon from '@material-ui/icons/PictureAsPdf';
import { useSnackbar } from 'notistack';

import { Main } from '../../../../components/Main';
import api from '../../../../services/api';
import {
  generateAnnualAuditPDF,
  IAnnualAudit,
  TIPO_LABELS,
} from '../../templates/annualAuditReport';

// Relatório Anual de Auditoria da unidade (tenant = companyDocument do
// login). Prévia na tela + PDF consolidado + CSVs detalhados (por dia e
// por rota) — todos da MESMA fonte, então os totais batem.

const MESES = [
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro',
];

const fmt = (n: number, dec = 0) =>
  Number(n).toLocaleString('pt-BR', {
    minimumFractionDigits: dec,
    maximumFractionDigits: dec,
  });

function baixarCsv(nome: string, cabecalho: string[], linhas: string[][]) {
  const esc = (v: string) =>
    /[";\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
  const conteudo = [cabecalho, ...linhas]
    .map((linha) => linha.map(esc).join(';'))
    .join('\r\n');
  // BOM para o Excel abrir acentos corretamente
  const blob = new Blob(['﻿' + conteudo], {
    type: 'text/csv;charset=utf-8',
  });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = nome;
  a.click();
  URL.revokeObjectURL(a.href);
}

const TIPO_KEYS = [
  'delivery',
  'collect',
  'reverse',
  'devolution',
  'cleaning',
  'maintenance',
  'other',
];

export const AnnualAuditReport = () => {
  const { enqueueSnackbar } = useSnackbar();
  const anoAtual = new Date().getFullYear();
  const [year, setYear] = useState(anoAtual);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<IAnnualAudit | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/summarizers/annual-audit', {
        params: { year },
      });
      setData(response.data);
    } catch (error) {
      enqueueSnackbar('Erro ao carregar o relatório', { variant: 'error' });
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar, year]);

  useEffect(() => {
    load();
  }, [load]);

  const handlePdf = useCallback(async () => {
    if (!data) return;
    setBusy(true);
    try {
      const params = await api
        .get('/company-params/by/document')
        .catch(() => null);
      await generateAnnualAuditPDF(data, params?.data?.reportLogoBase64);
    } catch (error) {
      enqueueSnackbar('Erro ao gerar o PDF', { variant: 'error' });
    } finally {
      setBusy(false);
    }
  }, [data, enqueueSnackbar]);

  const handleCsvs = useCallback(async () => {
    setBusy(true);
    try {
      const [daily, perRoute] = await Promise.all([
        api.get('/summarizers/annual-audit', {
          params: { year, detail: 'daily' },
        }),
        api.get('/summarizers/annual-audit', {
          params: { year, detail: 'per-route' },
        }),
      ]);
      const tipoCols = TIPO_KEYS.map((k) => TIPO_LABELS[k]);
      baixarCsv(
        `rotas-por-dia-${year}.csv`,
        ['Data', 'Motorista', 'Rotas', 'Paradas', ...tipoCols],
        daily.data.map((r: any) => [
          r.day,
          r.driverName,
          String(r.routes),
          String(r.stops),
          ...TIPO_KEYS.map((k) => String(r.byType[k] || 0)),
        ])
      );
      baixarCsv(
        `paradas-por-rota-${year}.csv`,
        ['Data', 'Rota', 'Motorista', 'Veículo', 'Paradas', ...tipoCols],
        perRoute.data.map((r: any) => [
          r.day,
          r.routeName,
          r.driverName,
          r.vehicleName,
          String(r.stops),
          ...TIPO_KEYS.map((k) => String(r.byType[k] || 0)),
        ])
      );
      enqueueSnackbar('CSVs baixados.', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar('Erro ao gerar os CSVs', { variant: 'error' });
    } finally {
      setBusy(false);
    }
  }, [enqueueSnackbar, year]);

  return (
    <Main title="Auditoria Anual">
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Box display="flex" alignItems="center" gridGap={12} flexWrap="wrap">
            <TextField
              select
              size="small"
              variant="outlined"
              label="Ano"
              value={year}
              onChange={(event) => setYear(Number(event.target.value))}
              style={{ width: 120 }}
            >
              {[anoAtual, anoAtual - 1, anoAtual - 2, anoAtual - 3].map(
                (y) => (
                  <MenuItem key={y} value={y}>
                    {y}
                  </MenuItem>
                )
              )}
            </TextField>
            <Button
              variant="contained"
              color="primary"
              startIcon={<PictureAsPdfIcon />}
              onClick={handlePdf}
              disabled={busy || loading || !data}
            >
              Baixar PDF
            </Button>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<GetAppIcon />}
              onClick={handleCsvs}
              disabled={busy || loading}
            >
              Baixar CSVs detalhados
            </Button>
            {data?.partial ? (
              <Typography variant="body2" color="textSecondary">
                Ano em andamento — números parciais.
              </Typography>
            ) : null}
          </Box>
        </Grid>

        {loading ? (
          <Grid item xs={12}>
            <Box textAlign="center" py={6}>
              <CircularProgress />
            </Box>
          </Grid>
        ) : !data ? null : (
          <>
            <Grid item xs={12}>
              <Grid container spacing={1}>
                {[
                  ['Veículos', fmt(data.counters.vehicles)],
                  ['Motoristas', fmt(data.counters.drivers)],
                  ['Rotas no ano', fmt(data.counters.routes)],
                  ['Paradas no ano', fmt(data.counters.stops)],
                  ['Rotas/dia', fmt(data.counters.routesPerDay, 1)],
                  ['Paradas/rota', fmt(data.counters.stopsPerRoute, 1)],
                ].map(([label, valor]) => (
                  <Grid item xs={6} sm={4} md={2} key={label}>
                    <Paper style={{ padding: 12, textAlign: 'center' }}>
                      <Typography variant="h6">{valor}</Typography>
                      <Typography variant="caption" color="textSecondary">
                        {label}
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                Rotas e paradas por mês
              </Typography>
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Mês</TableCell>
                      <TableCell align="right">Rotas</TableCell>
                      <TableCell align="right">Rotas/dia</TableCell>
                      <TableCell align="right">Paradas</TableCell>
                      <TableCell align="right">Paradas/rota</TableCell>
                      {TIPO_KEYS.map((k) => (
                        <TableCell key={k} align="right">
                          {TIPO_LABELS[k]}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.monthly.map((m) => (
                      <TableRow key={m.month} hover>
                        <TableCell>{MESES[m.month - 1]}</TableCell>
                        <TableCell align="right">{fmt(m.routes)}</TableCell>
                        <TableCell align="right">
                          {fmt(m.routesPerDay, 1)}
                        </TableCell>
                        <TableCell align="right">{fmt(m.stops)}</TableCell>
                        <TableCell align="right">
                          {fmt(m.stopsPerRoute, 1)}
                        </TableCell>
                        {TIPO_KEYS.map((k) => (
                          <TableCell key={k} align="right">
                            {fmt(m.byType[k] || 0)}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                Por motorista
              </Typography>
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Motorista</TableCell>
                      <TableCell align="right">Rotas</TableCell>
                      <TableCell align="right">Rotas/mês</TableCell>
                      <TableCell align="right">Rotas/dia</TableCell>
                      <TableCell align="right">Paradas</TableCell>
                      <TableCell align="right">Paradas/rota</TableCell>
                      {TIPO_KEYS.map((k) => (
                        <TableCell key={k} align="right">
                          {TIPO_LABELS[k]}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.byDriver.map((d) => (
                      <TableRow key={d.driverName} hover>
                        <TableCell>{d.driverName}</TableCell>
                        <TableCell align="right">{fmt(d.routes)}</TableCell>
                        <TableCell align="right">
                          {fmt(d.months ? d.routes / d.months : 0, 1)}
                        </TableCell>
                        <TableCell align="right">
                          {fmt(d.days ? d.routes / d.days : 0, 1)}
                        </TableCell>
                        <TableCell align="right">{fmt(d.stops)}</TableCell>
                        <TableCell align="right">
                          {fmt(d.stopsPerRoute, 1)}
                        </TableCell>
                        {TIPO_KEYS.map((k) => (
                          <TableCell key={k} align="right">
                            {fmt(d.byType[k] || 0)}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          </>
        )}
      </Grid>
    </Main>
  );
};
